<script setup lang="ts">
import FlowController from '@components/flow-controller'
import { IconTicket } from '@tabler/icons-vue'
import { type Component, computed, nextTick, ref, useId } from 'vue'

import BaseIcon from '@tix/ds/components/atoms/BaseIcon.vue'
import { isFlowSoldOut } from '@tix/entity'
import { type ApplicationError, type Schema } from '@tix/openapi-sdk'

import { formatGalleryImages } from '@/helpers/imageHelpers'

import { errorTracker } from '@/services/errorTracking'

import ImageGallery from '@/components/ImageGallery.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import BundleHeader from '@/components/bundles/BundleHeader.vue'
import BundleInformation from '@/components/bundles/BundleInformation.vue'
import FlowStepper from '@/components/bundles/FlowStepper.vue'
import ConfirmAction from '@/components/bundles/actions/ConfirmAction.vue'
import PickDateAction from '@/components/bundles/actions/PickDateAction.vue'
import PickNAction from '@/components/bundles/actions/PickNAction.vue'
import PickTimeAction from '@/components/bundles/actions/PickTimeAction.vue'
import PickVariantsAction from '@/components/bundles/actions/PickVariantsAction.vue'
import SurveyAction from '@/components/bundles/actions/SurveyAction.vue'
import SelectionSummary from '@/components/bundles/selections/SelectionSummary.vue'
import SelectionsTrigger from '@/components/bundles/selections/SelectionsTrigger.vue'

import { useFlowSelections } from '@/composables/useFlowSelections'

export type FlowDetailsViewProps = {
  id: string
  cartId: string
  flowDefinition: Schema<'flow_definition_min'>
  /** If `true`, the full bundle information is displayed. By default, the compact header is displayed. */
  isDetailedMode?: boolean
}

const props = defineProps<FlowDetailsViewProps>()

const emit = defineEmits<{
  error: [ApplicationError]
  checkout: []
  keepShopping: []
}>()

const galleryImages = computed(() => formatGalleryImages(props.flowDefinition.meta?.images))

const soldOut = computed(() => isFlowSoldOut(props.flowDefinition.meta))

const { rows, subtotal, selectionsLayout, showSummary, showTrigger, editStep } = useFlowSelections(() => soldOut.value)

/** Lets the summary landmark borrow its own heading as a name, rather than repeating the text. */
const summaryTitleId = useId()

const actions: Record<string, Component> = {
  pick_n: PickNAction,
  pick_variants: PickVariantsAction,
  pick_date: PickDateAction,
  pick_time: PickTimeAction,
  survey: SurveyAction,
  confirm: ConfirmAction,
}

const hrRef = ref<HTMLHRElement | null>(null)

const onStepChange = () => {
  nextTick(() => {
    const bottomOfHr = hrRef.value?.getBoundingClientRect().bottom ?? 0
    // scroll to the pixel just below the hr
    const scrollTarget = window.scrollY + bottomOfHr + 1
    window.scrollTo({ top: scrollTarget, behavior: 'smooth' })
  })
}
</script>

<template>
  <ImageGallery v-if="props.isDetailedMode && galleryImages.length > 0" class="image-gallery" :images="galleryImages" />

  <div class="main-content" :class="selectionsLayout ? 'with-selections' : 'single-column'">
    <!-- The selections layout lifts the header above the content area; without it, it stays inside the flow container. -->
    <div v-if="props.isDetailedMode || selectionsLayout" class="bundle-header-container">
      <BundleInformation v-if="props.isDetailedMode" :meta="flowDefinition.meta" :name="flowDefinition.name" />
      <BundleHeader v-else :meta="flowDefinition.meta" :name="flowDefinition.name" />

      <hr ref="hrRef" />
    </div>

    <div class="flow-layout-row" :class="{ 'without-summary': !showSummary }">
      <div class="flow-container">
        <template v-if="!props.isDetailedMode && !selectionsLayout">
          <BundleHeader :meta="flowDefinition.meta" :name="flowDefinition.name" />

          <hr ref="hrRef" />
        </template>

        <div v-if="soldOut" class="sold-out-message">
          <div class="sold-out-message-icon-container">
            <BaseIcon class="sold-out-message-icon" :icon="IconTicket" />
          </div>
          <div>Currently sold out</div>
        </div>

        <template v-else>
          <Suspense>
            <FlowController
              :actions="actions"
              :cartId="props.cartId"
              :errorTracker="errorTracker"
              :flowDefinitionId="props.id"
              :stepperComponent="FlowStepper"
              @checkout="emit('checkout')"
              @error="emit('error', $event)"
              @keep-shopping="emit('keepShopping')"
              @step-change="onStepChange"
            />

            <template #fallback>
              <LoadingSpinner :size="24" />
            </template>
          </Suspense>

          <SelectionsTrigger
            v-if="showTrigger"
            class="selections-trigger-narrow"
            :rows="rows"
            :subtotal="subtotal"
            @edit="editStep"
          />
        </template>
      </div>

      <div v-if="showSummary" class="selections-column">
        <aside :aria-labelledby="summaryTitleId">
          <SelectionSummary
            :rows="rows"
            :subtotal="subtotal"
            title="Your selections"
            :titleId="summaryTitleId"
            @edit="editStep"
          />
        </aside>
      </div>
    </div>
  </div>
</template>

<style scoped>
.image-gallery {
  margin-top: var(--space-2);
}

.main-content.single-column {
  padding: var(--space-1) 0 var(--space-1);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);

  .bundle-header-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .flow-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-1-5); /* same as figma's --padding-comfy */
    margin-inline: auto;
    max-width: 768px;
    width: 100%;

    hr {
      margin: 0;
    }

    .sold-out-message {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-1);
      font-size: var(--size-xl);
      font-weight: var(--font-weight-bold);

      .sold-out-message-icon-container {
        position: relative;
        rotate: 45deg;

        /* cross bar */
        &::before {
          content: '';
          position: absolute;
          top: 20px;
          left: -12px;
          background-color: var(--error-text-color);
          width: 70px;
          height: 3px;
        }

        .sold-out-message-icon.base-icon {
          color: var(--error-text-color);
          /* Size the BaseIcon box to the token (overrides 1.5em default). */
          width: var(--size-8xl);
          height: var(--size-8xl);
        }
      }
    }
  }

  @media (--desktop-experience) {
    padding: var(--space-1-5) 0 var(--space-1-5);
  }
}

/* Layout with the selections column — the header is lifted above both columns. */
.main-content.with-selections {
  --_flow-max-width: 768px;
  --_aside-width: 421px;

  container-type: inline-size;
  padding: var(--space-1) 0 var(--space-1);

  display: flex;
  flex-direction: column;
  gap: var(--space-1-5);

  @media (--desktop-experience) {
  }

  hr {
    margin: 0;
  }

  .flow-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-1-5); /* same as figma's --padding-comfy */
    width: 100%;

    .sold-out-message {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-1);
      font-size: var(--size-xl);
      font-weight: var(--font-weight-bold);

      .sold-out-message-icon-container {
        position: relative;
        rotate: 45deg;

        /* cross bar */
        &::before {
          content: '';
          position: absolute;
          top: 20px;
          left: -12px;
          background-color: var(--error-text-color);
          width: 70px;
          height: 3px;
        }

        .sold-out-message-icon.base-icon {
          color: var(--error-text-color);
          /* Size the BaseIcon box to the token (overrides 1.5em default). */
          width: var(--size-8xl);
          height: var(--size-8xl);
        }
      }
    }
  }

  .bundle-header-container,
  .flow-layout-row {
    margin-inline: auto;
    width: 100%;
    max-width: var(--_flow-max-width);
  }

  .bundle-header-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-1-5);
  }

  .flow-layout-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-2);
    align-items: start;
    margin-top: var(--padding-comfy);
  }

  .selections-column {
    display: none;
  }

  @media (--desktop-experience) {
    padding: var(--space-1-5) 0 var(--space-1-5);

    .bundle-header-container,
    .flow-layout-row {
      max-width: calc(var(--_flow-max-width) + var(--space-2) + var(--_aside-width));
    }

    .flow-layout-row {
      grid-template-columns: minmax(0, 1fr) var(--_aside-width);
    }

    /* Review drops the column but keeps the page width, so the step centres under the header. */
    .flow-layout-row.without-summary {
      grid-template-columns: minmax(0, 1fr);

      .flow-container {
        margin-inline: auto;
        max-width: var(--_flow-max-width);
      }
    }

    .selections-column {
      display: block;
    }

    .selections-trigger-narrow {
      display: none;
    }
  }
}
</style>
